import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as styles from './styles.css';
import { trackEmptyListingShow } from './tracking';
import {
  closeAndResetListingModal,
  EListingModalMode,
  EOffersFetchingMode,
} from '../../../common/actions/listingModal';
import { ETestId } from '../../../common/constants/testId';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';
import { OfferProvider } from '../../../common/containers/OfferCardContainer/OfferCardContext';
import { selectIsModalOpen, selectModalMode, selectOffersFetchingMode } from '../../../common/selectors/listingModal';
import { selectIsOffersLoading, selectListingOffers } from '../../../common/selectors/listingOffers';
import { selectQueryStringWithQMark } from '../../../common/selectors/queryString';
import { selectResultsJsonQuery } from '../../../common/selectors/results';
import { TThunkDispatch } from '../../../common/types/redux';
import { OnMapButton } from '../../components';
import { ListingSkeleton } from '../../components/OfferCard/components/OfferCardSkeleton';
import { OfferPreviewError } from '../../components/OfferCardPreview/components/OfferPreviewError';
import { OfferPreviewLoading } from '../../components/OfferCardPreview/components/OfferPreviewLoading';
import { trackMapButtonClick } from '../../tracking';
import { BreadcrumbsContainer } from '../Breadcrumbs/BreadcrumbsContainer';
import { EmptyListingContainer } from '../EmptyListingContainer';
import { JournalHighlightsContainer } from '../JournalHighlightsContainer';
import { ListingContainer } from '../Listing/ListingContainer';
import { OfferCardPreviewContainer } from '../OfferCardPreviewContainer';
import { PaginationControlsContainer } from '../PaginationControls';
import { SeoCrossLinksContainer } from '../SeoCrossLinks/SeoCrossLinksContainer';
import { SeoMarkedListContainer } from '../SeoMarkedList/SeoMarkedListContainer';
import { SeoTabsLinksContainer } from '../SeoTabsLinksContainer';

export const ListingMobileContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const { config } = useApplicationContext();
  const isSeoEnabled = config.get<boolean>('dailyrent.seo.Enabled');
  const isEmptyListingEventFiredRef = React.useRef(false);

  const offers = useSelector(selectListingOffers);
  const isLoading = useSelector(selectIsOffersLoading);
  const offersFetchingMode = useSelector(selectOffersFetchingMode);
  const offersModalMode = useSelector(selectModalMode);
  const queryString = useSelector(selectQueryStringWithQMark);
  const isListingModalOpen = useSelector(selectIsModalOpen);

  const isSingleOfferMode = offersFetchingMode === EOffersFetchingMode.SINGLE;
  const showMapButton = !isSingleOfferMode && offersModalMode === EListingModalMode.FULL_SCREEN;
  const bodyClassName = isSingleOfferMode ? undefined : styles['with-background'];

  const onMapButtonClick = React.useCallback(() => {
    trackMapButtonClick(jsonQuery, queryString);
    dispatch(closeAndResetListingModal());
  }, [dispatch, jsonQuery, queryString]);

  const onClose = React.useCallback(() => {
    dispatch(closeAndResetListingModal());
  }, [dispatch]);

  const onEmptyListingShow = React.useCallback(() => {
    if (!isEmptyListingEventFiredRef.current && isListingModalOpen) {
      trackEmptyListingShow({ jsonQuery, queryString });
      isEmptyListingEventFiredRef.current = true;
    }
  }, [jsonQuery, queryString, isListingModalOpen]);

  React.useEffect(() => {
    // Если после вызова события пустого листинга снова появились офферы,
    // то сбрасываем флаг, для возможного повторного вызова события, если офферов не будет снова
    if (isEmptyListingEventFiredRef.current && offers.length) {
      isEmptyListingEventFiredRef.current = false;
    }
  }, [offers.length]);

  if (isLoading) {
    return isSingleOfferMode ? (
      <OfferPreviewLoading />
    ) : (
      <div id={ETestId.ListingBody} className={bodyClassName}>
        <ListingSkeleton count={3} />
      </div>
    );
  }

  if (!offers.length && isListingModalOpen) {
    return isSingleOfferMode ? (
      <OfferPreviewError onClose={onClose} />
    ) : (
      <div id={ETestId.ListingBody} className={bodyClassName}>
        <EmptyListingContainer onShow={onEmptyListingShow} />
      </div>
    );
  }

  return (
    <div id={ETestId.ListingBody} className={bodyClassName}>
      {isSingleOfferMode ? (
        <OfferProvider key={offers[0].id} offer={offers[0]} offerPosition={1} offersLength={1}>
          <OfferCardPreviewContainer />
        </OfferProvider>
      ) : (
        <>
          <ListingContainer offers={offers} />
          {!isSingleOfferMode && <PaginationControlsContainer />}
          {!isSingleOfferMode && isSeoEnabled && <BreadcrumbsContainer offers={offers} />}
          {!isSingleOfferMode && isSeoEnabled && <JournalHighlightsContainer />}
          {!isSingleOfferMode && isSeoEnabled && <SeoTabsLinksContainer />}
          {!isSingleOfferMode && isSeoEnabled && <SeoCrossLinksContainer />}
          {!isSingleOfferMode && isSeoEnabled && <SeoMarkedListContainer />}
        </>
      )}

      {showMapButton && <OnMapButton onClick={onMapButtonClick} />}
    </div>
  );
};
